import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Camera, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export function PhotoGallery({ entityType, entityId, canUpload = true, canDelete = true, maxPhotos = null, photoStage = '', title = 'Property Photos', uploadLabel = 'Upload', emptyText = 'No photos yet', onPhotosChange = null }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [lightbox, setLightbox] = useState(null);

  const fetchPhotos = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get('/photos', { params: { entity_type: entityType, entity_id: entityId } });
      const filtered = photoStage ? data.filter(photo => photo.photo_stage === photoStage) : data;
      setPhotos(filtered);
      if (onPhotosChange) onPhotosChange(filtered, data);
    } catch { return; }
    finally { setLoading(false); }
  }, [entityType, entityId, photoStage, onPhotosChange]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const remaining = maxPhotos ? Math.max(0, maxPhotos - photos.length) : files.length;
    if (remaining <= 0) return;
    setUploadError('');
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, remaining)) {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams({ entity_type: entityType, entity_id: entityId });
        if (photoStage) params.set('photo_stage', photoStage);
        await api.post(`/photos/upload?${params.toString()}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await fetchPhotos();
    } catch {
      setUploadError('Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/photos/${fileId}`);
      fetchPhotos();
    } catch { return; }
  };

  const getPhotoUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/photos/file/${fileId}`;
  const testSuffix = `${entityType}-${entityId}${photoStage ? `-${photoStage}` : ''}`;

  if (loading) {
    return <div className="flex gap-2">{[1,2,3].map(i => <div key={i} className="w-20 h-20 bg-zinc-200 rounded-md animate-pulse" />)}</div>;
  }

  return (
    <div data-testid={`photo-gallery-${entityType}-${entityId}`}>
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1" data-testid={`photo-gallery-title-${testSuffix}`}>
          <Camera className="w-3 h-3" /> {title} ({photos.length})
        </h4>
        {canUpload && (
          <div className={`grid grid-cols-2 gap-2 sm:flex ${maxPhotos && photos.length >= maxPhotos ? 'pointer-events-none opacity-50' : ''}`} data-testid={`photo-upload-actions-${testSuffix}`}>
            <label className="cursor-pointer" data-testid={`photo-camera-button-${testSuffix}`}>
              <input type="file" accept="image/*" capture="environment" multiple onChange={handleUpload} className="hidden" data-testid={`photo-camera-input-${testSuffix}`} />
              <span className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-black text-white transition-[transform,background-color] duration-150 hover:bg-blue-700 active:scale-95">
                <Camera className="w-5 h-5" /> {uploading ? 'Uploading...' : maxPhotos && photos.length >= maxPhotos ? 'Limit' : 'Camera'}
              </span>
            </label>
            <label className="cursor-pointer" data-testid={`photo-upload-button-${testSuffix}`}>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/*" multiple onChange={handleUpload} className="hidden" data-testid={`photo-upload-input-${testSuffix}`} />
              <span className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-white px-4 py-2 text-sm font-black text-blue-700 transition-[transform,background-color] duration-150 hover:bg-blue-50 active:scale-95">
                <Upload className="w-5 h-5" /> {uploading ? 'Uploading...' : maxPhotos && photos.length >= maxPhotos ? 'Limit' : uploadLabel}
              </span>
            </label>
          </div>
        )}
      </div>
      {uploadError && <p className="mb-2 rounded-2xl bg-red-50 px-3 py-2 text-sm font-bold text-red-700" data-testid={`photo-upload-error-${testSuffix}`}>{uploadError}</p>}

      {photos.length === 0 ? (
        <div className="flex items-center justify-center rounded-3xl border-2 border-dashed border-blue-200 bg-blue-50 p-6">
          <div className="text-center">
            <ImageIcon className="w-10 h-10 text-blue-300 mx-auto mb-1" />
            <p className="text-sm font-bold text-blue-700" data-testid={`photo-empty-${testSuffix}`}>{emptyText}</p>
            {canUpload && <p className="mt-1 text-xs text-blue-500" data-testid={`photo-empty-help-${testSuffix}`}>Take a photo or upload from camera roll/device</p>}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {photos.map((photo) => (
            <div key={photo.file_id || photo.id} className="relative group aspect-square rounded-md overflow-hidden border border-zinc-200 bg-zinc-100" data-testid={`photo-card-${photo.file_id}`}>
              <img
                src={getPhotoUrl(photo.file_id)}
                alt={photo.original_filename}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => setLightbox(photo)}
                loading="lazy"
                  data-testid={`photo-thumb-${photo.file_id}`}
              />
              {canDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(photo.file_id); }}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`photo-delete-${photo.file_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                {photo.original_filename}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-1 bg-black border-none">
          {lightbox && (
            <div className="relative">
              <img
                src={getPhotoUrl(lightbox.file_id)}
                alt={lightbox.original_filename}
                className="w-full max-h-[80vh] object-contain rounded"
                data-testid="photo-lightbox-image"
              />
              <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                <span className="text-white text-xs bg-black/70 px-2 py-1 rounded">{lightbox.original_filename}</span>
                <span className="text-white/70 text-xs bg-black/70 px-2 py-1 rounded">
                  {lightbox.uploaded_by_name} - {new Date(lightbox.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Simplified version for public intake form (no auth)
export function PublicPhotoUpload({ onPhotosChange, maxPhotos = 5 }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState('');

  const handleAdd = (e) => {
    const newFiles = [...e.target.files];
    const imageFiles = newFiles.filter(f => f.type?.startsWith('image/'));
    const remaining = Math.max(0, maxPhotos - files.length);
    const accepted = imageFiles.slice(0, remaining);
    if (newFiles.length !== imageFiles.length) setError('Only image files are accepted.');
    else if (accepted.length < newFiles.length) setError(`Maximum ${maxPhotos} photos per lead.`);
    else setError('');
    const allFiles = [...files, ...accepted];
    setFiles(allFiles);
    const newPreviews = accepted.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...newPreviews]);
    onPhotosChange(allFiles);
    e.target.value = '';
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFiles(newFiles);
    setPreviews(newPreviews);
    onPhotosChange(newFiles);
  };

  return (
    <div data-testid="public-photo-upload">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-zinc-700">Property Photos</span>
        <label className="cursor-pointer" data-testid="public-photo-add-button">
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleAdd} disabled={files.length >= maxPhotos} className="hidden" data-testid="public-photo-file-input" />
          <span className="flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200 px-2.5 py-1.5 rounded-md transition-colors">
            <Camera className="w-3 h-3" /> {files.length >= maxPhotos ? 'Max Photos' : 'Add Photos'}
          </span>
        </label>
      </div>
      <p className="mb-2 text-[10px] text-zinc-400" data-testid="public-photo-limit-text">Up to {maxPhotos} images.</p>
      {error && <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-600" data-testid="public-photo-error">{error}</p>}
      {previews.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {previews.map((src, i) => {
            const previewKey = `${files[i]?.name || 'photo'}-${files[i]?.lastModified || i}`;
            return <div key={previewKey} className="relative aspect-square rounded-md overflow-hidden border border-zinc-200 group">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`public-photo-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>;
          })}
        </div>
      )}
      {previews.length === 0 && (
        <p className="text-xs text-zinc-400">Upload photos of the property for accurate quoting</p>
      )}
    </div>
  );
}
